import { Injectable } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from "@angular/forms";

import {
    ForbrugsnotifikationIndstillinger,
    ForbrugsnotifikationSektionIndstilling,
    ForbrugsnotifikationUdsendelseIndstilling,
    ForbrugsnotifikationUdsendelsesintervaller,
    ForbrugsnotifikationUdsendelsestyper
} from "../model/forbrugsnotifikation-indstillinger.model";
import {
    ForbrugsnotifikationFrekvens,
    ForbrugsnotifikationOpsaetning,
    ForbrugsnotifikationSektionOpsaetning,
    ForbrugsnotifikationType,
    ForbrugsnotifikationUdsendelseOpsaetning
} from "../model/forbrugsnotifikation-opsaetning.model";

@Injectable({ providedIn: "root" })
export class ForbrugsnotifikationFormBuilder {
    public formGroupToOpsaetning(formGroup: UntypedFormGroup): ForbrugsnotifikationOpsaetning {
        const udsendelseType = formGroup.get("udsendelse.type").value;
        const afmeldt = udsendelseType !== ForbrugsnotifikationType.sms && udsendelseType !== ForbrugsnotifikationType.mail;
        const result: ForbrugsnotifikationOpsaetning = {
            forbrug: this.formGroupToSektion(afmeldt, formGroup.get("forbrug") as UntypedFormGroup),
            retur: this.formGroupToSektion(afmeldt, formGroup.get("retur") as UntypedFormGroup),
            udsendelse: this.formGroupToUdsendelse(afmeldt, formGroup.get("udsendelse") as UntypedFormGroup)
        };
        return result;
    }

    public opsaetningToFormGroup(
        opsaetning: ForbrugsnotifikationOpsaetning,
        indstillinger: ForbrugsnotifikationIndstillinger
    ): UntypedFormGroup {
        const result = new UntypedFormGroup({
            forbrug: this.sektionToFormGroup(opsaetning.forbrug, indstillinger.forbrug),
            retur: this.sektionToFormGroup(opsaetning.retur, indstillinger.retur),
            udsendelse: this.udsendelseToFormGroup(opsaetning.udsendelse, indstillinger.udsendelse)
        });
        this.valueChanges(result);
        result.valueChanges.subscribe(() => this.valueChanges(result));
        return result;
    }

    private valueChanges(formGroup: UntypedFormGroup): void {
        // Opdater validatorer for mail og mobil
        const type = formGroup.get("udsendelse.type").value as ForbrugsnotifikationType;
        const mobilRequired = type === ForbrugsnotifikationType.sms;
        const mailRequired = type === ForbrugsnotifikationType.mail;
        this.setValidators(formGroup, "udsendelse.mail", mailRequired ? [Validators.required, Validators.email] : []);
        this.setValidators(formGroup, "udsendelse.mobil", mobilRequired ? [Validators.required, Validators.pattern("\\d+")] : []);
    }

    private setValidators(formGroup: UntypedFormGroup, path: string, validators: ValidatorFn[]): void {
        formGroup.get(path).setValidators(validators);
        // Bemærk: Hvis vi tilføjer { onlySelf: true }, vil det give fejlen ExpressionChangedAfterItHasBeenCheckedError.
        formGroup.get(path).updateValueAndValidity({ emitEvent: false });
    }

    private sektionToFormGroup(
        sektion: ForbrugsnotifikationSektionOpsaetning,
        indstilling: ForbrugsnotifikationSektionIndstilling
    ): UntypedFormGroup {
        const brugerdefineret = indstilling.brugerdefineret.synlig && (sektion.brugerdefineret || !indstilling.standard.synlig);
        const result = new UntypedFormGroup({
            brugerdefineret: new UntypedFormControl(brugerdefineret),
            vaerdi: new UntypedFormControl(sektion.vaerdi)
        });
        return result;
    }

    private formGroupToSektion(afmeldt: boolean, formGroup: UntypedFormGroup): ForbrugsnotifikationSektionOpsaetning {
        const result = {
            brugerdefineret: afmeldt ? false : formGroup.get("brugerdefineret").value,
            vaerdi: formGroup.get("vaerdi").value
        };
        return result;
    }

    private udsendelseToFormGroup(
        udsendelse: ForbrugsnotifikationUdsendelseOpsaetning,
        indstillinger: ForbrugsnotifikationUdsendelseIndstilling
    ): UntypedFormGroup {
        const type = this.justerTilValgbarType(udsendelse.type, indstillinger.typer);
        const frekvens = this.justerTilValgbarFrekvens(udsendelse.frekvens, indstillinger.intervaller);
        const result = new UntypedFormGroup({
            type: new UntypedFormControl(type, [Validators.required]),
            frekvens: new UntypedFormControl(frekvens, [Validators.required]),
            mail: new UntypedFormControl(udsendelse.mail, [Validators.required, Validators.email]),
            mobil: new UntypedFormControl(udsendelse.mobil, [Validators.required])
        });
        return result;
    }

    private justerTilValgbarType(
        type: ForbrugsnotifikationType,
        aktiveredeTyper: ForbrugsnotifikationUdsendelsestyper
    ): ForbrugsnotifikationType {
        const typer = new Map<ForbrugsnotifikationType, ForbrugsnotifikationType>([
            [ForbrugsnotifikationType.sms, aktiveredeTyper.sms ? ForbrugsnotifikationType.sms : null],
            [ForbrugsnotifikationType.mail, aktiveredeTyper.mail ? ForbrugsnotifikationType.mail : null]
        ]);
        return (
            typer.get(type) ||
            typer.get(ForbrugsnotifikationType.mail) ||
            typer.get(ForbrugsnotifikationType.sms) ||
            ForbrugsnotifikationType.mail
        ); // Fallback i det usandsylige tilfælde at ingen typer er aktiveret
    }

    private justerTilValgbarFrekvens(
        frekvens: ForbrugsnotifikationFrekvens,
        intervaller: ForbrugsnotifikationUdsendelsesintervaller
    ): ForbrugsnotifikationFrekvens {
        const frekvenser = new Map<ForbrugsnotifikationFrekvens, ForbrugsnotifikationFrekvens>([
            [ForbrugsnotifikationFrekvens.maanedlig, intervaller.maanedlig ? ForbrugsnotifikationFrekvens.maanedlig : null],
            [ForbrugsnotifikationFrekvens.ugentlig, intervaller.ugentlig ? ForbrugsnotifikationFrekvens.ugentlig : null],
            [ForbrugsnotifikationFrekvens.daglig, intervaller.daglig ? ForbrugsnotifikationFrekvens.daglig : null]
        ]);
        // Hvis den valgte/gemte frekvens er synlig, vælges den.
        // Ellers vælges den højste af de frekvens man kan vælge mellem.
        // Prioritet: Månedlig, Ugentlig, Daglig
        return (
            frekvenser.get(frekvens) ||
            frekvenser.get(ForbrugsnotifikationFrekvens.maanedlig) ||
            frekvenser.get(ForbrugsnotifikationFrekvens.ugentlig) ||
            frekvenser.get(ForbrugsnotifikationFrekvens.daglig) ||
            ForbrugsnotifikationFrekvens.maanedlig // Fallback i det usandsynlige tilfælde at ingen frekvenser er aktiveret i Umbraco
        );
    }

    private formGroupToUdsendelse(afmeldt: boolean, formGroup: UntypedFormGroup): ForbrugsnotifikationUdsendelseOpsaetning {
        const result = {
            type: afmeldt ? null : formGroup.get("type").value,
            frekvens: afmeldt ? null : formGroup.get("frekvens").value,
            mail: afmeldt ? null : formGroup.get("mail").value,
            mobil: afmeldt ? null : formGroup.get("mobil").value
        };
        return result;
    }
}
